/**
 * Message Item Component
 * 
 * Renders a single chat message with actions (bookmark, edit, regenerate, branch).
 * Memoized for performance optimization.
 */

import { memo } from "react";

import {
  Edit2,
  RotateCcw,
  Check,
  X,
  Bookmark,
  BookmarkCheck,
  GitBranch,
  MoreVertical,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { CollapsibleMarkdown } from "@/components/ui/collapsible-markdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";

import type { Message } from "@/types/state";

interface MessageItemProps {
  message: Message;
  isBookmarked: boolean;
  isEditing: boolean;
  editingContent: string;
  isRegenerating: boolean;
  messageRef: (el: HTMLDivElement | null) => void;
  chatCollapseEnabled: boolean;
  chatCollapsedHeight: number;
  onToggleBookmark: () => void;
  onBranchFromMessage: () => void;
  onStartEditing: () => void;
  onStartRegenerating: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditingContentChange: (content: string) => void;
}

const MessageItemComponent = ({
  message,
  isBookmarked,
  isEditing,
  editingContent,
  isRegenerating,
  messageRef,
  chatCollapseEnabled,
  chatCollapsedHeight,
  onToggleBookmark,
  onBranchFromMessage,
  onStartEditing,
  onStartRegenerating,
  onSaveEdit,
  onCancelEdit,
  onEditingContentChange,
}: MessageItemProps) => {
  return (
    <div
      ref={messageRef}
      className={`group flex ${
        message.role === "user" ? "justify-end" : "justify-start"
      } gap-2 transition-all rounded-lg`}
    >
      <div
        className={`max-w-[80%] rounded-lg px-4 py-3 ${
          message.role === "user"
            ? "bg-primary text-primary-foreground"
            : "bg-muted"
        }`}
      >
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editingContent}
              onChange={(e) => onEditingContentChange(e.target.value)}
              className="min-h-[80px]"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={onCancelEdit}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onSaveEdit}
                disabled={!editingContent.trim()}
              >
                <Check className="h-4 w-4 mr-1" />
                Save & Regenerate
              </Button>
            </div>
          </div>
        ) : (
          <>
            {message.role === "assistant" ? (
              <CollapsibleMarkdown
                content={message.content}
                collapseEnabled={chatCollapseEnabled}
                collapsedHeight={chatCollapsedHeight}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            )}
          </>
        )}
      </div>
      
      {!isEditing && (
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleBookmark}
            title={isBookmarked ? "Remove bookmark" : "Bookmark message"}
            className="h-8 w-8 p-0"
          >
            {isBookmarked ? (
              <BookmarkCheck className="h-4 w-4 text-primary" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                title="More options"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={onBranchFromMessage}>
                <GitBranch className="h-4 w-4 mr-2" />
                Branch from here
              </DropdownMenuItem>
              
              {message.role === "user" && (
                <DropdownMenuItem onClick={onStartEditing}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit message
                </DropdownMenuItem>
              )}
              
              {message.role === "assistant" && (
                <DropdownMenuItem
                  onClick={onStartRegenerating}
                  disabled={isRegenerating}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Regenerate response
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

export const MessageItem = memo(MessageItemComponent);
